import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useToastMessageState } from '../../ToastMessage/ToastMessageProvider';
import {
  createNoteComment,
  deleteNoteComment,
  updateNoteComment,
} from '../../../services/queries/MaggieNoteCommentsQueries';
import { NoteComment } from '../../../data-models/note.data-model';
import { noteCommentsStateJ } from '../../../services/state/NoteCommentsStateJ';
import { getErrorMessage } from '../../../services/queryHelpers';
import { ICommentUpdate } from '../../../pages/DealFlow2/data-models/comment.data-model';

export type INoteCommentUpdateParams = ICommentUpdate;

export function useNoteCommentActions(noteId: number) {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const [isLoading, setIsLoading] = useState(false);
  const [ongoinActionId, setOngoinActionId] = useState<number | null>(null);

  const createCallback = useCallback(
    async (get: Getter, set: Setter, newCommentData: NoteComment) => {
      setIsLoading(true);

      try {
        const response = await createNoteComment(newCommentData);
        // Read the latest comments at success time (not before the API call)
        // so a concurrent update/delete that landed in-flight is preserved.
        const current = await get(noteCommentsStateJ(noteId));
        set(noteCommentsStateJ(noteId), [response, ...current]);
        return response;
      } catch (err) {
        console.error(err);
        // No optimistic write to roll back here.
        pushErrorToast({ message: 'Failed to create comment' });
      } finally {
        setIsLoading(false);
      }
    },
    [noteId, pushErrorToast]
  );

  const updateCallback = useCallback(
    async (get: Getter, set: Setter, { comment, commentId }: INoteCommentUpdateParams) => {
      setIsLoading(true);
      setOngoinActionId(commentId);
      const prev = await get(noteCommentsStateJ(noteId));

      // Apply the optimistic update before awaiting the API. We keep a
      // reference to `prev` and restore it directly on error.
      const optimistic = prev.map((c) => (c.id === commentId ? { ...c, comment } : c));
      set(noteCommentsStateJ(noteId), optimistic);

      try {
        const updatedComment = await updateNoteComment({ noteId, comment, commentId });
        pushSuccessToast({ message: 'Comment successfully updated' });
        return updatedComment;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'An error occurred while trying to update comment');
        pushErrorToast({ message });
        set(noteCommentsStateJ(noteId), prev);
      } finally {
        setIsLoading(false);
        setOngoinActionId(null);
      }
    },
    [noteId, pushErrorToast, pushSuccessToast]
  );

  const deleteCallback = useCallback(
    async (get: Getter, set: Setter, commentId: number) => {
      setIsLoading(true);
      setOngoinActionId(commentId);
      const prev = await get(noteCommentsStateJ(noteId));

      const optimistic = prev.filter((c) => c.id !== commentId);
      set(noteCommentsStateJ(noteId), optimistic);

      try {
        const deletedComment = await deleteNoteComment(commentId);
        pushSuccessToast({ message: 'Comment was deleted successfully' });
        return deletedComment;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'An error occurred while trying to delete comment');
        pushErrorToast({ message });
        set(noteCommentsStateJ(noteId), prev);
      } finally {
        setIsLoading(false);
        setOngoinActionId(null);
      }
    },
    [noteId, pushErrorToast, pushSuccessToast]
  );

  // Names preserved (`useCreate*`, `useUpdate*`, `useDelete*`) for consumer
  // back-compat even though these are now plain bound callbacks, not hooks.
  const useCreateNoteComment = useAtomCallback(createCallback);
  const useUpdateNoteComment = useAtomCallback(updateCallback);
  const useDeleteNoteComment = useAtomCallback(deleteCallback);

  return {
    isLoading,
    ongoinActionId,
    useDeleteNoteComment,
    useCreateNoteComment,
    useUpdateNoteComment,
  };
}
